import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Drawer, Tooltip, message } from 'antd';
import { CloseOutlined, CustomerServiceOutlined, PhoneOutlined } from '@ant-design/icons';
import { ConversationProvider, useConversation } from '@elevenlabs/react';
import type { Language } from '@elevenlabs/react';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import CompanyContext from '../../../contexts/company/CompanyContext';
import { getCompanyAiReceptionistTestSession } from '../../../../domain/use_cases/ai-receptionist/getCompanyAiReceptionistTestSession';

const TEST_MAX_DURATION_SECONDS = 5 * 60;
const TEST_WARNING_SECONDS = 30;

interface TranscriptEntry {
  role: 'user' | 'agent';
  message: string;
}

interface TestReceptionistSectionProps {
  assistantName: string;
  subscriptionId: string;
}

const TestReceptionistConversation: React.FC<TestReceptionistSectionProps> = ({ assistantName, subscriptionId }) => {
  const { authentication } = useContext(AuthenticationContext);
  const { activeCompany } = useContext(CompanyContext);

  const [isStarting, setIsStarting] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);

  const conversation = useConversation({
    onMessage: (payload) => setTranscript((prev) => [...prev, { role: payload.role, message: payload.message }]),
    onError: (errorMessage) => message.error(errorMessage),
  });

  const name = assistantName || 'Donna';
  const isActive = conversation.status === 'connected' || conversation.status === 'connecting';
  const statusLabel =
    conversation.status === 'connecting'
      ? 'Connexion…'
      : conversation.status === 'connected'
        ? conversation.isSpeaking
          ? `${name} parle`
          : `${name} vous écoute`
        : 'Conversation terminée';

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript.length]);

  // Call timer: the test is billed by the minute, like a real call
  useEffect(() => {
    if (conversation.status !== 'connected') return;
    const startedAt = Date.now();
    const interval = setInterval(() => setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(interval);
  }, [conversation.status]);

  // A test left running in a forgotten tab hangs up by itself. Only caps the UI: the hard limit for any
  // session is the max duration set on the ElevenLabs agent
  useEffect(() => {
    if (conversation.status !== 'connected' || elapsedSeconds < TEST_MAX_DURATION_SECONDS) return;
    conversation.endSession();
    message.info(`Test arrêté au bout de ${TEST_MAX_DURATION_SECONDS / 60} minutes`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsedSeconds]);

  const isNearLimit = elapsedSeconds >= TEST_MAX_DURATION_SECONDS - TEST_WARNING_SECONDS;
  const elapsedLabel = `${Math.floor(elapsedSeconds / 60)}:${String(elapsedSeconds % 60).padStart(2, '0')}`;
  const stageModifier =
    conversation.status !== 'connected' ? 'idle' : conversation.isSpeaking ? 'speaking' : 'listening';

  const startTest = async () => {
    if (!activeCompany?.id || !authentication) return;
    setIsStarting(true);
    try {
      // Ask for the mic first so a refusal fails before a token is minted. The SDK opens its own
      // stream, so this one is released right away (otherwise the mic stays on after hanging up).
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());

      const session = await getCompanyAiReceptionistTestSession(
        { company_id: activeCompany.id, subscription_id: subscriptionId },
        authentication,
      );
      // Page left while waiting for the mic or the token: the provider already cleaned up on unmount,
      // a session started now would run (and bill) with no panel to hang up
      if (!isMountedRef.current) return;
      const { agent, tts } = session.conversation_config_override;

      setTranscript([]);
      setElapsedSeconds(0);
      setIsPanelOpen(true);
      conversation.startSession({
        conversationToken: session.conversation_token,
        connectionType: 'webrtc',
        overrides: {
          agent: {
            prompt: agent.prompt,
            firstMessage: agent.first_message,
            language: agent.language as Language,
          },
          ...(tts?.voice_id && { tts: { voiceId: tts.voice_id } }),
        },
        dynamicVariables: session.dynamic_variables,
      });
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Impossible de démarrer le test';
      message.error(errorMessage);
    } finally {
      setIsStarting(false);
    }
  };

  // Closing the panel hangs up: no session keeps running (and billing) out of sight
  const closePanel = () => {
    if (isActive) conversation.endSession();
    setIsPanelOpen(false);
  };

  return (
    <>
      {isActive ? (
        <Button onClick={() => setIsPanelOpen(true)}>Voir la conversation</Button>
      ) : (
        <Tooltip
          title={`Parlez à ${name} comme un appelant, sans passer d’appel. Elle utilise les réglages enregistrés : enregistrez avant de tester vos changements.`}
        >
          <Button onClick={startTest} loading={isStarting}>
            Appeler mon assistante
          </Button>
        </Tooltip>
      )}

      <Drawer
        title={`Tester ${name}`}
        open={isPanelOpen}
        onClose={closePanel}
        width={440}
        closable={false}
        extra={<Button type="text" icon={<CloseOutlined />} onClick={closePanel} />}
        footer={
          isActive ? (
            <div className="receptionist-ai-detail-page__test-hangup">
              <Button
                type="primary"
                danger
                shape="circle"
                size="large"
                icon={<PhoneOutlined rotate={225} />}
                onClick={() => conversation.endSession()}
                aria-label="Raccrocher"
              />
            </div>
          ) : (
            <div className="receptionist-ai-detail-page__drawer-footer">
              <Button onClick={closePanel}>Fermer</Button>
              <Button type="primary" onClick={startTest} loading={isStarting}>
                Recommencer
              </Button>
            </div>
          )
        }
      >
        <div className={`receptionist-ai-detail-page__test-stage receptionist-ai-detail-page__test-stage--${stageModifier}`}>
          <div className="receptionist-ai-detail-page__test-orb">
            <CustomerServiceOutlined />
          </div>
          <span className="receptionist-ai-detail-page__test-name">{name}</span>
          <span
            className={`receptionist-ai-detail-page__test-status${isNearLimit && conversation.status === 'connected' ? ' receptionist-ai-detail-page__test-status--warning' : ''}`}
          >
            {statusLabel}
            {conversation.status === 'connected' && ` · ${elapsedLabel}`}
            {conversation.status === 'connected' && isNearLimit && ' · fin du test imminente'}
          </span>
        </div>

        <div className="receptionist-ai-detail-page__test-transcript">
          {transcript.length === 0 && conversation.status === 'connected' && (
            <p className="receptionist-ai-detail-page__test-empty">{name} va décrocher, parlez normalement.</p>
          )}
          {transcript.map((entry, index) => (
            <div
              key={index}
              className={`receptionist-ai-detail-page__test-bubble receptionist-ai-detail-page__test-bubble--${entry.role}`}
            >
              {entry.message}
            </div>
          ))}
          <div ref={transcriptEndRef} />
        </div>
      </Drawer>
    </>
  );
};

// The provider ends the session on unmount (leaving the page hangs up)
const TestReceptionistSection: React.FC<TestReceptionistSectionProps> = (props) => (
  <ConversationProvider>
    <TestReceptionistConversation {...props} />
  </ConversationProvider>
);

export default TestReceptionistSection;
